import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { formatMoney, posErrorText } from './ItemConfiguration'
import { fetchVoucher, posOrderingError } from './service'
import { cn } from '@/lib/utils'
import { CARD, MAIN, QUIET } from './posStyles'
import type { PosVoucher } from './types'

/** Checks a code before it touches the payment, so staff see the balance and the remaining value first. */
export function VoucherEntry({ restaurantId, dueCents, onApply }: { restaurantId: number; dueCents: number; onApply: (voucher: PosVoucher) => void }) {
  const { t, i18n } = useTranslation('posOrdering')
  const [code, setCode] = useState('')
  const [found, setFound] = useState<PosVoucher | null>(null)
  const [problem, setProblem] = useState('')
  const [busy, setBusy] = useState(false)
  const money = (cents: number) => formatMoney(i18n.language, cents / 100, 'EUR')
  async function check() {
    setBusy(true); setProblem(''); setFound(null)
    try {
      const voucher = await fetchVoucher(restaurantId, code.trim())
      if (voucher.expired) setProblem(t('voucher.expired'))
      else if (voucher.balanceCents <= 0) setProblem(posErrorText(t, 'voucher_empty'))
      else setFound(voucher)
    } catch (failure) { setProblem(posErrorText(t, posOrderingError(failure).code)) }
    finally { setBusy(false) }
  }
  return <div className={cn(CARD, 'space-y-3 p-3')}>
    <div className="flex gap-2">
      <Input aria-label={t('voucher.code')} placeholder={t('voucher.code')} value={code} autoCapitalize="characters" autoCorrect="off" maxLength={20} className="h-14 min-w-0 flex-1 rounded-xl border-0 bg-secondary text-lg font-semibold uppercase tracking-wider focus-visible:ring-offset-0"
        onChange={event => { setCode(event.target.value); setFound(null); setProblem('') }}
        onKeyDown={event => { if (event.key === 'Enter' && !busy && code.trim().length >= 4) void check() }} />
      <Button type="button" variant="ghost" className={cn(QUIET, 'h-14 px-5 text-base')} disabled={busy || code.trim().length < 4} onClick={() => { void check() }}>
        {busy ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : t('voucher.check')}</Button>
    </div>
    {problem && <p role="alert" className="text-sm text-destructive">{problem}</p>}
    {found && <div className="space-y-2">
      <p className="text-[15px] font-semibold text-pos-plus">{t('voucher.valid')}</p>
      <dl className="space-y-1 text-base">
        <div className="flex items-baseline gap-2"><dt>{t('voucher.balance')}</dt><span aria-hidden className="pos-leader" /><dd className="font-semibold">{money(found.balanceCents)}</dd></div>
        <div className="flex items-baseline gap-2"><dt>{t('voucher.expires')}</dt><span aria-hidden className="pos-leader" /><dd>{new Date(found.expiresOn).toLocaleDateString(i18n.language)}</dd></div>
        <div className="flex items-baseline gap-2"><dt>{t('voucher.after')}</dt><span aria-hidden className="pos-leader" /><dd>{money(Math.max(0, found.balanceCents - dueCents))}</dd></div>
      </dl>
      <Button type="button" className={cn(MAIN, 'h-14 w-full text-lg')} onClick={() => onApply(found)}>{t('voucher.apply')}</Button>
    </div>}
  </div>
}
